import { ChatBotData, putApiChatBotDataRename, useDeleteApiChatBotDataDelete, useGetApiChatBotDataList, useGetApiWorkspaceGet, usePostApiChatBotDataCreate, usePostApiChatBotDataImport } from "@/API"
import { AutoResetFileButton } from "@/Components/AutoResetFileButton";
import { PageTitle } from "@/Components/PageTitle";
import { useAuthStore } from "@/Stores/AuthStore";
import { ActionIcon, Box, Breadcrumbs, Button, FileButton, Flex, Group, LoadingOverlay, Pagination, Stack, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconEdit, IconFileImport, IconFilePlus, IconTrash } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { DataTable } from "mantine-datatable";
import { useRef, useState } from "react";
import { useDeleteChatBotData } from "./Helpers/DeleteChatBotData";
import { useRenameChatBotData } from "./Helpers/RenameChatBotData";
import { AppBreadcrumbs } from "@/Components/AppBreadcrumbs";

export function ChatBotDataList() {
    const { workspaceId } = useParams({ from: "/workspace/$workspaceId/chatBotData/" })
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const workspace = useGetApiWorkspaceGet({ id: workspaceId })

    const PAGE_SIZE = 10
    const [activePage, setPage] = useState(1);
    const list = useGetApiChatBotDataList({ workspaceId: workspaceId, skip: (activePage - 1) * PAGE_SIZE, limit: PAGE_SIZE })

    const { confirmAndDelete } = useDeleteChatBotData()
    const create_Mutation = usePostApiChatBotDataCreate()
    const { renameChatBotData } = useRenameChatBotData()
    const import_Mutation = usePostApiChatBotDataImport()

    const [createLoading, setCreateLoading] = useState(false)
    return <Stack gap={5} pos="relative">
        <LoadingOverlay visible={list.isFetching} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
        <AppBreadcrumbs
            items={[
                {
                    label: '工作空間列表',
                    linkProps: { to: '/workspace' },
                },
                {
                    label: workspace.data?.data.name ?? '',
                    linkProps: { to: '/workspace/$workspaceId', params: { workspaceId } },
                },
                {
                    label: '機器人設定',
                    active: true,
                }
            ]}
        />
        <Group>
            <Button loading={createLoading} leftSection={<IconFilePlus />} onClick={() => {
                modals.openContextModal({
                    modal: "TextInput",
                    title: "創建聊天機器人",
                    innerProps: {
                        textInputProps: {
                            label: "聊天機器人名稱"
                        },
                        onConfirm: async v => {
                            setCreateLoading(true)
                            try {
                                await create_Mutation.mutateAsync({
                                    params: {
                                        workspaceId: workspaceId,
                                        name: v
                                    }
                                })
                                queryClient.invalidateQueries({ queryKey: list.queryKey })
                                notifications.show({ message: "創建成功" })

                            } catch (e) {
                                notifications.show({ message: "創建失敗", color: "red" })

                            }
                            setCreateLoading(false)
                        }
                    }
                })
            }}>創建聊天機器人</Button>
            <AutoResetFileButton accept="application/json" onChange={async f => {
                const text = await f?.text()
                if (text) {
                    const data = JSON.parse(text) as ChatBotData
                    modals.openContextModal({
                        modal: "TextInput",
                        title: "匯入聊天機器人",
                        innerProps: {
                            initText: data.name,
                            textInputProps: {
                                label: "聊天機器人名稱"
                            },
                            onConfirm: async v => {
                                setCreateLoading(true)
                                data.name = v
                                try {
                                    await import_Mutation.mutateAsync({
                                        params: {
                                            workspaceId: workspaceId
                                        },
                                        data: {
                                            importData: data
                                        }
                                    })
                                    queryClient.invalidateQueries({ queryKey: list.queryKey })
                                    notifications.show({ message: "匯入成功" })
                                } catch (e) {
                                    notifications.show({ message: "匯入失敗", color: "red" })
                                }
                                setCreateLoading(false)
                            }
                        }
                    })
                } else {
                    notifications.show({ message: "讀取資料失敗", color: "red" })
                }
            }}>
                {(props) => <Button {...props} loading={createLoading} leftSection={<IconFileImport />}>
                    匯入
                </Button>}
            </AutoResetFileButton>
        </Group>
        <Pagination value={activePage} onChange={setPage} total={Math.ceil((list.data?.data.total ?? 0) / PAGE_SIZE)} />
        <DataTable
            withTableBorder
            minHeight={list.data?.data.results && list.data?.data.results?.length > 0 ? 0 : 150}
            noRecordsText="沒有資料"
            columns={[
                { accessor: 'name', title: "名稱" },
                {
                    accessor: 'actions',
                    title: <Box mr={6}>操作</Box>,
                    textAlign: 'right',
                    render: (data) => (
                        <Group gap={4} justify="right" wrap="nowrap">
                            <ActionIcon
                                size="sm"
                                variant="subtle"
                                onClick={() => {
                                    renameChatBotData(data)
                                }}
                            >
                                <IconEdit size={16} />
                            </ActionIcon>
                            <ActionIcon
                                size="sm"
                                variant="subtle"
                                color="red"
                                onClick={() => {
                                    confirmAndDelete(data)
                                }}
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        </Group>
                    ),
                },
            ]}
            onRowDoubleClick={({ record }) => {
                navigate({
                    to: "/workspace/$workspaceId/chatBotData/$chatBotDataId", params: {
                        workspaceId: workspaceId,
                        chatBotDataId: record.id!
                    }
                })
            }}
            records={list.data?.data.results ?? []}
        />
    </Stack>
}